import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { apiUrl } from "../auth/config";
import { useAuth } from "../auth/AuthContext";
import "../styles/app.css";
import "../styles/board.css";

const FILTER_STATUSES = [
  { value: "", label: "All statuses" },
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "qualified", label: "Qualified" },
  { value: "retained", label: "Retained" },
  { value: "closed_lost", label: "Closed lost" },
];

const BOARD_COLUMNS = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "qualified", label: "Qualified" },
  { value: "retained", label: "Retained" },
  { value: "closed_lost", label: "Closed lost" },
];

const REFERRALS = [
  { value: "", label: "All referrals" },
  { value: "organic", label: "Organic" },
  { value: "piboost_fb", label: "PIBOOST · Facebook" },
  { value: "piboost_insta", label: "PIBOOST · Instagram" },
  { value: "piboost_linkedin", label: "PIBOOST · LinkedIn" },
];

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function statusLabel(status) {
  return (status || "new").replace(/_/g, " ");
}

function normalizeStatus(status) {
  const s = status || "new";
  return BOARD_COLUMNS.some((c) => c.value === s) ? s : "new";
}

function emailLocalPart(address) {
  if (!address) return null;
  const at = String(address).indexOf("@");
  return at > 0 ? String(address).slice(0, at) : String(address);
}

function leadLastTouchedAt(lead) {
  const times = [
    lead.updatedAt,
    lead.lastContactedAt,
    lead.scheduledUpdatedAt,
    lead.createdAt,
  ].filter(Boolean);
  if (times.length === 0) return null;
  return times.reduce((a, b) => (String(a) >= String(b) ? a : b));
}

function LeadMetaLines({ lead }) {
  const assignee =
    emailLocalPart(lead.assigneeEmail) || lead.assigneeEmail || "Unassigned";
  const updated = leadLastTouchedAt(lead);
  return (
    <dl className="lead-meta">
      <div>
        <dt>Received</dt>
        <dd>
          <time dateTime={lead.createdAt || undefined}>
            {formatWhen(lead.createdAt)}
          </time>
        </dd>
      </div>
      <div>
        <dt>Updated</dt>
        <dd>
          <time dateTime={updated || undefined}>{formatWhen(updated)}</time>
        </dd>
      </div>
      <div>
        <dt>Assignee</dt>
        <dd title={lead.assigneeEmail || undefined}>{assignee}</dd>
      </div>
    </dl>
  );
}

export default function Leads() {
  const { user, loading, isAuthenticated, isAdmin, logout, getIdToken } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [error, setError] = useState("");
  const [pendingApproval, setPendingApproval] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [referral, setReferral] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [qDraft, setQDraft] = useState("");
  const [view, setView] = useState("board");
  const [movingId, setMovingId] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const draggedIdRef = useRef(null);
  const suppressClickRef = useRef(false);

  const load = useCallback(async () => {
    setFetching(true);
    setError("");
    setPendingApproval(false);
    try {
      const token = await getIdToken();
      const params = new URLSearchParams();
      if (referral) params.set("referral", referral);
      // Board needs every column; ignore status filter in board view.
      if (view === "list" && status) params.set("status", status);
      if (q) params.set("q", q);
      const qs = params.toString();
      const res = await fetch(`${apiUrl}/leads${qs ? `?${qs}` : ""}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 403) {
        const data = await res.json().catch(() => ({}));
        if (data.error === "pending_approval") {
          setPendingApproval(true);
          setLeads([]);
          return;
        }
        throw new Error(data.message || "Access denied");
      }
      if (!res.ok) {
        throw new Error(`Could not load leads (${res.status})`);
      }
      const data = await res.json();
      setLeads(Array.isArray(data.leads) ? data.leads : []);
    } catch (err) {
      setError(err.message || "Failed to load leads");
      setLeads([]);
    } finally {
      setFetching(false);
    }
  }, [getIdToken, referral, status, q, view]);

  useEffect(() => {
    if (!isAuthenticated) return;
    load();
  }, [isAuthenticated, load]);

  const columns = useMemo(() => {
    const map = Object.fromEntries(BOARD_COLUMNS.map((c) => [c.value, []]));
    for (const lead of leads) {
      const key = normalizeStatus(lead.status);
      map[key].push(lead);
    }
    for (const key of Object.keys(map)) {
      map[key].sort((a, b) =>
        String(b.createdAt || "").localeCompare(String(a.createdAt || ""))
      );
    }
    return map;
  }, [leads]);

  const countLabel = useMemo(() => {
    if (fetching) return "Loading…";
    return `${leads.length} lead${leads.length === 1 ? "" : "s"}`;
  }, [fetching, leads.length]);

  async function moveLead(leadId, nextStatus) {
    const lead = leads.find((l) => l.leadId === leadId);
    if (!lead) return;
    const current = normalizeStatus(lead.status);
    if (current === nextStatus) return;

    setMovingId(leadId);
    setError("");
    const prev = leads;
    setLeads((list) =>
      list.map((l) =>
        l.leadId === leadId ? { ...l, status: nextStatus } : l
      )
    );

    try {
      const token = await getIdToken();
      const res = await fetch(
        `${apiUrl}/leads/${encodeURIComponent(leadId)}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "content-type": "application/json",
          },
          body: JSON.stringify({ status: nextStatus }),
        }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Could not update status (${res.status})`);
      }
      const data = await res.json();
      if (data.lead) {
        setLeads((list) =>
          list.map((l) => (l.leadId === leadId ? data.lead : l))
        );
      }
    } catch (err) {
      setLeads(prev);
      setError(err.message || "Move failed");
    } finally {
      setMovingId(null);
    }
  }

  if (loading) {
    return (
      <div className="app-shell">
        <main className="app-main">
          <p className="leads-support">Loading…</p>
        </main>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }

  return (
    <div className="app-shell">
      <header className="app-header">
        <Link className="app-header__brand" to="/">
          FTOJ
        </Link>
        <div className="app-header__meta">
          <Link to="/leads">Inbox</Link>
          {isAdmin ? <Link to="/proofs">Proofs</Link> : null}
          <Link to="/security">Security</Link>
          <span>Jones &amp; Booth</span>
          <span>{user?.signInDetails?.loginId || user?.username}</span>
          <button className="btn btn--ghost" type="button" onClick={() => logout()}>
            Sign out
          </button>
        </div>
      </header>

      <main className={`app-main${view === "board" ? " app-main--wide" : ""}`}>
        <div className="leads-heading">
          <div>
            <h1 className="leads-title">
              {view === "board" ? "Lead board" : "Lead inbox"}
            </h1>
            <p className="leads-support">
              {view === "board"
                ? "Drag cards to update status · working a lead assigns it to you · click for detail"
                : "Newest first · Jones & Booth intake from jonesboothlaw.com"}
            </p>
          </div>
          <div className="leads-heading-actions">
            <div className="view-toggle" role="group" aria-label="View mode">
              <button
                type="button"
                className={`view-toggle__btn${view === "board" ? " is-active" : ""}`}
                onClick={() => setView("board")}
              >
                Board
              </button>
              <button
                type="button"
                className={`view-toggle__btn${view === "list" ? " is-active" : ""}`}
                onClick={() => setView("list")}
              >
                List
              </button>
            </div>
            <button
              className="btn btn--ghost"
              type="button"
              onClick={load}
              disabled={fetching}
            >
              Refresh
            </button>
          </div>
        </div>

        <form
          className={`leads-filters${view === "board" ? " leads-filters--board" : ""}`}
          onSubmit={(e) => {
            e.preventDefault();
            setQ(qDraft.trim());
          }}
        >
          <label>
            <span className="sr-only">Search</span>
            <input
              type="search"
              placeholder="Search name, email, phone…"
              value={qDraft}
              onChange={(e) => setQDraft(e.target.value)}
            />
          </label>
          <label>
            <span className="sr-only">Referral</span>
            <select value={referral} onChange={(e) => setReferral(e.target.value)}>
              {REFERRALS.map((opt) => (
                <option key={opt.value || "all"} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          {view === "list" ? (
            <label>
              <span className="sr-only">Status</span>
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                {FILTER_STATUSES.map((opt) => (
                  <option key={opt.value || "all"} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <button className="btn btn--primary" type="submit">
            Search
          </button>
        </form>

        <p className="leads-count">{countLabel}</p>

        {pendingApproval ? (
          <section className="leads-panel">
            <h2>Pending admin approval</h2>
            <p>
              You’re signed in, but lead access requires confirmation. An approval
              email was sent to the FTOJ admin — ask them to click Approve, then
              hit Refresh.
            </p>
          </section>
        ) : null}

        {error ? <p className="auth-error">{error}</p> : null}

        {!fetching && !error && !pendingApproval && leads.length === 0 ? (
          <section className="leads-panel">
            <h2>No leads match</h2>
            <p>Try clearing filters, or submit a test form on jonesboothlaw.com.</p>
          </section>
        ) : null}

        {view === "list" ? (
          <ul className="leads-list">
            {leads.map((lead) => (
              <li key={lead.leadId}>
                <Link className="leads-row" to={`/leads/${lead.leadId}`}>
                  <div className="leads-row__main">
                    <p className="leads-row__name">
                      {lead.firstName} {lead.lastName}
                    </p>
                    <p className="leads-row__meta">
                      {lead.caseType || "Case TBD"}
                      {lead.referralLabel || lead.referralCode
                        ? ` · ${lead.referralLabel || lead.referralCode}`
                        : ""}
                    </p>
                    <LeadMetaLines lead={lead} />
                  </div>
                  <div className="leads-row__side">
                    <span
                      className={`leads-status-pill leads-status-pill--${lead.status || "new"}`}
                    >
                      {statusLabel(lead.status)}
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <div className="board" aria-label="Lead status board">
            {BOARD_COLUMNS.map((col) => (
              <section
                key={col.value}
                className={`board-col${dragOverCol === col.value ? " is-drop-target" : ""}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  setDragOverCol(col.value);
                }}
                onDragLeave={() => {
                  setDragOverCol((cur) => (cur === col.value ? null : cur));
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOverCol(null);
                  const id =
                    e.dataTransfer.getData("text/lead-id") ||
                    draggedIdRef.current;
                  draggedIdRef.current = null;
                  if (id) moveLead(id, col.value);
                }}
              >
                <header className="board-col__head">
                  <h2>{col.label}</h2>
                  <span className="board-col__count">
                    {(columns[col.value] || []).length}
                  </span>
                </header>
                <ul className="board-col__cards">
                  {(columns[col.value] || []).map((lead) => (
                    <li key={lead.leadId}>
                      <article
                        className={`board-card${movingId === lead.leadId ? " is-moving" : ""}`}
                        draggable
                        onDragStart={(e) => {
                          draggedIdRef.current = lead.leadId;
                          suppressClickRef.current = false;
                          e.dataTransfer.setData("text/lead-id", lead.leadId);
                          e.dataTransfer.effectAllowed = "move";
                        }}
                        onDragEnd={() => {
                          draggedIdRef.current = null;
                          setDragOverCol(null);
                          // Drag fires click after; suppress one navigation.
                          suppressClickRef.current = true;
                          window.setTimeout(() => {
                            suppressClickRef.current = false;
                          }, 50);
                        }}
                        onClick={() => {
                          if (suppressClickRef.current) return;
                          navigate(`/leads/${lead.leadId}`);
                        }}
                      >
                        <p className="board-card__name">
                          {lead.firstName} {lead.lastName}
                        </p>
                        <p className="board-card__meta">
                          {lead.caseType || "Case TBD"}
                        </p>
                        <p className="board-card__meta">
                          {lead.referralLabel || lead.referralCode || "Organic"}
                        </p>
                        <LeadMetaLines lead={lead} />
                      </article>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
